import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Row, Col, Flex, Dropdown, Button, Drawer, Typography } from "antd";
import {
  CloseOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import HeaderLogo from "../../assets/images/HeaderLogo.png";

import Sidebar from "../Sidebar";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { useUserSelfStore } from "../../store/apis/useUserStore";
import ProfileMenu from "./ProfileMenu";
import UserStatusHandler from "./UserStatusHandler";

const Header = () => {
  const searchInputRef = useRef();

  // Store selectors
  const userSelfData = useUserSelfStore((state) => state.userSelfData);
  const setSearchMsgText = useBridgeStore((state) => state.setSearchMsgText);
  const setGlobalSectionConfig = useBridgeStore(
    (store) => store.setGlobalSectionConfig
  );
  const theme = useBridgeStore((state) => state.theme);
  const setTheme = useBridgeStore((state) => state.setTheme);

  // Local state
  const [toggleSearchMsg, setToggleSearchMsg] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openSidebarMenu, setOpenSidebarMenu] = useState(false);

  // Memoized values
  const isNarrowScreen = useMemo(() => window.innerWidth <= 500, []);
  const shouldShowLogo = useMemo(
    () => (!toggleSearchMsg && isNarrowScreen) || !isNarrowScreen,
    [toggleSearchMsg, isNarrowScreen]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Search functionality
  const handleSearch = useCallback(() => {
    setSearchMsgText({ value: searchText, timestamp: new Date() });
    setGlobalSectionConfig({
      openedSection: "MessageSearchSection",
      isExpanded: false,
    });
  }, [searchText, setSearchMsgText, setGlobalSectionConfig]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // Sidebar controls
  const showSidebarMenu = useCallback(() => {
    setOpenSidebarMenu(true);
  }, []);

  const onClose = useCallback(() => {
    setOpenSidebarMenu(false);
  }, []);

  const handleCloseSearch = useCallback(() => {
    if (searchText.length === 0) {
      setToggleSearchMsg(false);
    }
  }, [searchText.length]);

  // Effects
  useEffect(() => {
    setTheme(theme);
  }, []);

  return (
    <>
      <Row className="header-wrapper">
        <Col flex="auto">
          <Flex align="center" gap="small">
            <Flex align="center" className="change-chat-prev-next">
              <button className="btn-prev" onClick={showSidebarMenu}></button>
              <div className="channel-wrapper">
                <Button
                  type="text"
                  onClick={showSidebarMenu}
                  className="sidebar-toggler"
                >
                  {openSidebarMenu ? (
                    <MenuFoldOutlined />
                  ) : (
                    <MenuUnfoldOutlined />
                  )}
                </Button>
                <Drawer
                  title={
                    <Flex vertical>
                      <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        {userSelfData?.fullName || userSelfData?.username}
                      </Typography.Title>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: "12px" }}
                      >
                        {userSelfData?.email}
                      </Typography.Text>
                    </Flex>
                  }
                  placement="left"
                  closable={false}
                  onClose={onClose}
                  open={openSidebarMenu}
                  className="mobile-drawer-sidebar"
                  width="300"
                >
                  <Flex vertical className="ec-sidebar ec-sidebar--drawer">
                    <Sidebar />
                  </Flex>
                </Drawer>
              </div>
            </Flex>
            {shouldShowLogo && (
              <div className="sidebar-logo">
                <img src={HeaderLogo} alt="Header Logo" />
              </div>
            )}
          </Flex>
        </Col>

        <Col flex="auto">
          <Dropdown
            className="ec-dropdown header-search-dropdown"
            dropdownRender={ (menu) => (
              <Flex
                vertical
                gap="small"
                className="ec-dropdown__menu header-search-dropdown__menu"
              >
              </Flex>
            ) }
            trigger={["click"]}
          >
            <div
              className={`search-container ${
                toggleSearchMsg ? "msgSearchOpen" : ""
              }`}
              onChange={(e) => setSearchText(e.target.value)}
            >
              <Button
                onClick={() => setToggleSearchMsg(true)}
                className="search-icon"
              >
                <i className="fa-solid fa-magnifying-glass"></i>
              </Button>
              <input
                ref={searchInputRef}
                type="text"
                className="custom-search"
                placeholder="Search"
                value={searchText}
                onKeyDown={handleKeyPress}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText.length > 0 && (
                <div className="clear-search" onClick={() => setSearchText("")}>
                  <CloseOutlined />
                </div>
              )}
              <Button className="closeBtn" onClick={handleCloseSearch}>
                <i className="fa-solid fa-circle-chevron-right"></i>
              </Button>
            </div>
          </Dropdown>
        </Col>

        <Col flex="auto">
          <Flex
            justify="end"
            align="center"
            gap="small"
            className="menu-controls"
          >
            <div className="toggle-button">
              <input
                type="checkbox"
                className={`checkbox ${theme}-mode`}
                id="toggleBtn"
                checked={theme === "dark"}
                onChange={toggleTheme}
              />
              <label className="switch" htmlFor="toggleBtn">
                <i className="fas fa-moon"></i>
                <i className="fas fa-sun"></i>
              </label>
              <div className="ball"></div>
            </div>

            <ProfileMenu />
          </Flex>
        </Col>
      </Row>

      {/* <UserStatusHandler userData={userSelfData} /> */}
    </>
  );
};

export default Header;
